export interface AtelierBrief {
    challenge: string
    direction: string
    outcome: string
}

export interface AtelierSectionLabels {
    overview: string
    challenge: string
    direction: string
    outcome: string
}

export interface AtelierProject {
    id: string
    title: string
    eyebrow?: string
    category: string
    positioning: string
    thumbnailSrc: string
    thumbnailIsIcon?: boolean
    description: string
    brief: AtelierBrief
    sectionLabels?: AtelierSectionLabels
    gallerySrcs: string[]
    pos: {
        xPct: number
        yPct: number
    }
}

export const isVideoSrc = (src: string): boolean => /\.(mp4|webm|mov)(\?|#|$)/i.test(src)

export interface AtelierNote {
    id: string
    title: string
    date: string
    preview: string
    body: string
    pinned?: boolean
}

export interface AtelierPhoto {
    src: string
    ratio: 'tall' | 'wide' | 'square'
    caption: string
}

export type DockAppId = 'youtube' | 'instagram' | 'notes' | 'photos' | 'x' | 'mail' | 'trash'

export interface DockApp {
    id: DockAppId
    name: string
    href?: string
}

export const DESIGNER = {
    name: 'Alex Morgan',
    role: 'Independent product & brand designer',
    email: 'prokashghostt@gmail.com'
}

export const WALLPAPER_LIGHT = '/atelier/website-bg.jpg'
export const WALLPAPER_DARK = '/atelier/website-bg.jpg'

export const YOUTUBE_CHANNEL = 'https://www.youtube.com/@ghoshprokash'
export const INSTAGRAM_PROFILE = 'https://www.instagram.com/ghostpantheon/?hl=en'
export const VIDEO_EMBED = 'https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1&playsinline=1&rel=0'

const THUMBS: string[] = [
    '/atelier/projects/noir-leather.jpg',
    '/atelier/projects/kestrel-outerwear.jpg',
    '/atelier/projects/lume-eyewear.jpg',
    '/atelier/projects/pulp-press.jpg',
    '/atelier/projects/solenne-womenswear.jpg',
    '/atelier/projects/quiet-quarterly.jpg',
    '/atelier/projects/bloom-sneaker.jpg',
    '/atelier/projects/kew-botanical.jpg',
    '/atelier/projects/poppy-press.jpg',
    '/atelier/projects/saffron-issue.jpg'
]

const gallery = (offset: number): string[] => {
    const rotated = [...THUMBS.slice(offset), ...THUMBS.slice(0, offset)]

    return rotated.slice(0, 9)
}

export const PROJECTS: AtelierProject[] = [
    {
        id: 'vfx-compositing',
        title: 'Blender',
        eyebrow: '3D.01 — Project',
        category: 'Blender · After Effects',
        positioning: 'Making the fake stuff feel heavy and real.',
        thumbnailSrc: '/atelier/projects/blender.png',
        thumbnailIsIcon: true,
        description:
            'Heavy visual effects and compositing work to take a basic sequence and make it look massive. The whole goal was seamless 3D integration and building an atmosphere you can actually feel.',
        brief: {
            challenge:
                'The raw footage was completely flat and lacked any real scale. I needed to drop some massive digital elements into the scene, but if they looked like cheap, plastic CGI, it would ruin the entire video.',
            direction:
                'I basically lived in After Effects for this one. I had to nail the 3D tracking and do some intense masking just to get the base right. But the real magic was in the compositing: matching the shadows perfectly, blending the textures, and dialing in the lighting so the digital assets actually felt like they physically existed in the scene.',
            outcome:
                "The CGI blended so well you couldn't tell what was real and what was built in post. It completely flipped the mood, made the whole project look way more expensive, and most importantly, kept people glued to the screen instead of swiping away."
        },
        sectionLabels: {
            overview: 'The Vibe',
            challenge: 'The Problem',
            direction: 'What I Did',
            outcome: 'The Result'
        },
        gallerySrcs: [
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/vfx.jpg',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/shot.png',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/render-1.png',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/render-2.png',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/car-intro.gif',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/sequence-01.mp4',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/clip-140733.mp4',
            'https://atelier-desktop.cdn.modulify.website/projects/vfx-compositing/clip-2424.mp4'
        ],
        pos: { xPct: 56, yPct: 18 }
    },
    {
        id: 'solenne-womenswear',
        title: 'Solenne Womenswear',
        category: 'Web Design',
        positioning: 'A quiet-luxury label, given a quiet-luxury website.',
        thumbnailSrc: THUMBS[4],
        description:
            'End-to-end web design and build direction for a womenswear label. A slow, editorial commerce experience that treats the lookbook and the shop as one continuous scroll.',
        brief: {
            challenge:
                'Most fashion commerce feels like a spreadsheet with photos. Make browsing feel like turning pages.',
            direction:
                'Full-bleed imagery, type that gets out of the way, and a checkout that never breaks the spell.',
            outcome:
                'Average session time more than doubled. Return rate on first orders dropped sharply.'
        },
        gallerySrcs: gallery(5),
        pos: { xPct: 72, yPct: 26 }
    },
    {
        id: 'poppy-press',
        title: 'Poppy Press — Spring Issue',
        category: 'Editorial',
        positioning: 'A spring issue that smells like the page.',
        thumbnailSrc: THUMBS[8],
        description:
            "Cover and feature-well design for a magazine's spring issue. Pressed-flower art direction and a typographic system that loosens up as the issue goes on.",
        brief: {
            challenge:
                "Make a seasonal issue feel special without resetting the magazine's whole identity.",
            direction:
                'Let spring into the type. Pressed botanicals, a warmer paper stock, a cover that earns a second look.',
            outcome:
                'Best-selling issue of the year on the newsstand. The cover was reprinted as a limited poster.'
        },
        gallerySrcs: gallery(9),
        pos: { xPct: 62, yPct: 50 }
    },
    {
        id: 'saffron-issue',
        title: 'Saffron — A Yellow Issue',
        category: 'Editorial',
        positioning: 'One colour, one issue, all the way through.',
        thumbnailSrc: THUMBS[9],
        description:
            'Concept and design for a themed magazine issue built entirely around the colour yellow. A monochrome constraint applied to the cover, the type, and every spread inside.',
        brief: {
            challenge:
                'Sustain a single-colour concept across 90 pages without exhausting the reader.',
            direction:
                'Treat yellow as the grid, not the decoration. Shift temperature and saturation to mark each section.',
            outcome:
                "Picked up two editorial design awards. The most shared issue in the title's history."
        },
        gallerySrcs: gallery(0),
        pos: { xPct: 78, yPct: 44 }
    }
]

export const NOTES: AtelierNote[] = [
    {
        id: 'cv',
        title: 'Resume — Prokash Ghosh',
        date: 'Pinned',
        pinned: true,
        preview: 'Freelance Video Editor & VFX Artist.',
        body: `Freelance Video Editor & VFX Artist. Turning raw footage and rough concepts into dynamic, high-retention visual experiences.

EXPERIENCE
Independent — Video Editor & Motion Artist (2023–Present)
Specializing in video editing, motion graphics, animation, 3D, SaaS motion design, and visual effects.
Independent — Graphic Designer (2023)

SELECTED WORK
Short-form content creators, SaaS startups, independent brands, and anyone needing high-end motion graphics and visual effects.

CAPABILITIES
Video Editing · Motion Graphics · Visual Effects (VFX) · 3D Animation · SaaS Motion · Color Grading · Sound Design · The precise keyframe adjustments no one else wants to own.

CONTACT
prokashghostt@gmail.com — open to project work and the occasional good argument about pacing and render settings.`
    },
    {
        id: 'principles',
        title: 'How I Actually Work',
        date: 'Today',
        preview: "If it's boring, cut it. Motion needs a reason.",
        body: `1. If it’s boring, cut it.
Pacing is literally everything. Whether I’m editing a fast-paced short or a smooth SaaS animation, the rhythm has to lock the viewer in. If a frame isn't pushing the video forward, it doesn't make the final export.

2. Motion needs a reason.
I don't just throw in effects because they look cool. Every animation, 3D camera move, or text pop should guide the viewer's eye or make the message clearer. Movement is communication, not just decoration.

3. Kill the flat visuals.
Everything needs depth. I spend a lot of time on color grading, lighting, and textures to make the footage or 3D render feel cinematic and physical. A good grade and the right atmospheric lighting change the whole vibe.

4. Sound is half the video.
A massive VFX shot means nothing if it sounds empty. I build out heavy, layered sound design for every project. The right ambient noise, risers, and perfect audio-sync are what actually make an edit hit hard.

5. Obsess over the invisible stuff.
The difference between an okay edit and a high-end one usually lives in the graph editor. I spend way too much time tweaking custom easing curves, smoothing out masks, and getting the compositing exactly right. People might not see those micro details, but they definitely feel them.`
    },
    {
        id: 'clarity-essay',
        title: 'Why I care about clarity',
        date: 'Apr 14',
        preview: 'Attention is expensive and people scroll fast.',
        body: `Because attention is expensive and people scroll fast. If a video is confusing for even a single second, the viewer is gone.

Whether I'm explaining a complex SaaS product with motion graphics or cutting a high-energy short, the goal is always zero confusion. You can have the heaviest VFX, the perfect color grade, or the craziest 3D transitions, but if the audience doesn't immediately get what they're looking at, none of that matters.

For me, clarity isn't about making things basic or boring. It's about stripping away the visual noise so the actual message hits harder. It’s making sure that every cut, graphic, and sound effect works together to guide the viewer exactly where they need to go, effortlessly. When an edit is truly clear, the audience doesn't even notice the work they just feel the impact.`
    },
    {
        id: 'refining',
        title: 'Things I’m constantly working on',
        date: 'Mar 02',
        preview: 'Believable 3D, a faster workflow, the three-second rule.',
        body: `Making 3D look like it actually belongs there
I can't stand when 3D renders look like cheap plastic. I spend a lot of time in Blender just trying to make things feel heavy and physical. It usually comes down to endlessly messing with textures, matching the real-world lighting, and doing the heavy lifting in After Effects until the render actually feels like it exists in the shot.

Working faster than I can think
Bouncing between Premiere Pro, After Effects, and a dozen different plugins gets messy fast. I’m always tweaking how I work—cleaning up my folder structures, memorizing new hotkeys, and tightening my workflow. The goal is to get the software out of the way so I can edit and build things exactly as fast as I picture them in my head.

Surviving the three-second rule
Let's be real, attention spans are basically gone. You have about three seconds before someone swipes away. I spend a ton of time analyzing retention graphs and figuring out how to build better hooks. It’s basically a massive puzzle trying to find the exact mix of fast pacing, punchy text animation, and heavy sound design that actually forces people to stop scrolling.`
    }
]

export const PHOTOS: AtelierPhoto[] = [
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-1.jpg', ratio: 'wide', caption: 'Dance under the lights' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-2.jpg', ratio: 'tall', caption: 'Sunflower in her hair' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-3.jpg', ratio: 'tall', caption: 'A smile for Saraswati' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-4.jpg', ratio: 'tall', caption: 'Saraswati Puja' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-5.jpg', ratio: 'tall', caption: 'Waiting in the wings' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-6.jpg', ratio: 'tall', caption: 'Panjabi Edition' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-7.jpg', ratio: 'wide', caption: 'Calm Paradise' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-8.jpg', ratio: 'wide', caption: 'Hut by the river' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-9.jpg', ratio: 'wide', caption: 'Above the green' },
    { src: 'https://atelier-desktop.cdn.modulify.website/images/photos/photo-10.jpg', ratio: 'tall', caption: 'By the lake' }
]

export const DOCK_APPS: DockApp[] = [
    { id: 'youtube', name: 'YouTube', href: YOUTUBE_CHANNEL },
    { id: 'instagram', name: 'Instagram', href: INSTAGRAM_PROFILE },
    { id: 'notes', name: 'Notes' },
    { id: 'photos', name: 'Photos' },
    { id: 'x', name: 'X', href: 'https://x.com/prokash_ghosh24' },
    { id: 'mail', name: 'Mail' },
    { id: 'trash', name: 'Trash' }
]

export const X_PROFILE = {
    name: 'Elon Musk',
    handle: '@elonmusk',
    avatar: '/atelier/x_ui/Elon_pfp.jpg',
    cover: '/atelier/x_ui/elon_cover.jpeg',
    bio: 'Trying to make life multi-planetary. Free speech absolutist. Mostly jokes.',
    location: 'Mars',
    joined: 'Joined June 2009',
    following: '684',
    followers: '221.4M',
    posts: '209K posts',
    tweetLink: 'https://www.motionin.design',
    tweetSuffix: ' has the craziest ai animations ever'
}
